import React, { useEffect, useState } from "react";
import { CheckCircle2, XCircle } from "lucide-react";
import { FloorChart } from "./FloorChart";

const SENT = { bullish: "#10B981", bearish: "#F43F5E", neutral: "#94A3B8" };
const useCount = (key, max, ms) => {
  const [n, setN] = useState(0);
  useEffect(() => { setN(0); const t = setInterval(() => setN((x) => (x >= max ? x : x + 1)), ms); return () => clearInterval(t); }, [key, max, ms]);
  return n;
};

export const ScottDesk = ({ ev }) => {
  const rows = ev.payload.rows || [], n = useCount(ev.t_ms, rows.length, 140);
  return (
    <div className="space-y-1 nx-mono text-base" data-testid="desk-scott">
      <p className="text-cyan-200/60 text-sm">{ev.payload.note}</p>
      {rows.slice(0, n).slice(-9).map((r) => (
        <div key={r.ticker} className="flex items-center gap-3 nx-fade-up" data-testid={`scott-row-${r.ticker}`}>
          <b className="w-16 text-cyan-50">{r.ticker}</b><span className="w-14 text-cyan-200/60">{r.direction}</span>
          <span className="w-12 text-right text-cyan-100">{r.score}</span>
          <span className={`px-2 rounded text-xs font-bold ${r.kept ? "bg-emerald-500/20 text-emerald-300" : "bg-slate-500/20 text-slate-400"}`}>{r.kept ? "KEPT" : "DROPPED"}</span>
        </div>))}
    </div>
  );
};

export const JimmyDesk = ({ ev }) => {
  const p = ev.payload, c = SENT[p.sentiment] || SENT.neutral;
  return (
    <p className="text-xl font-semibold leading-snug nx-fade-up" style={{ color: c }} data-testid="desk-jimmy">
      {p.source} — {p.headline} — {(p.tickers || []).join("/")} — {p.sentiment}
    </p>
  );
};

const fmt = (v, s = "") => (v == null ? "n/a" : `${v}${s}`);
export const FundaDesk = ({ ev }) => {
  const p = ev.payload;
  const cells = [["Fwd P/E", fmt(p.forward_pe)], ["Trail P/E", fmt(p.trailing_pe)], ["Net margin", fmt(p.profit_margin, "%")], ["Op margin", fmt(p.operating_margin, "%")], ["Insider", p.insider_flow]];
  return (
    <div className="space-y-2 nx-fade-up" data-testid="desk-funda">
      <p className="text-cyan-50 font-bold text-lg">{p.ticker} · {p.name}</p>
      <div className="grid grid-cols-5 gap-2">{cells.map(([k, v]) => <div key={k} className="rounded-lg bg-white/5 px-2 py-1"><p className="text-[11px] text-cyan-200/50">{k}</p><p className="text-sm font-bold text-cyan-50">{v}</p></div>)}</div>
      <p className="text-sm text-lime-200/90 line-clamp-2">{p.verdict}</p>
    </div>
  );
};

export const PrakashDesk = ({ ev }) => (
  <div data-testid="desk-prakash">
    <p className="text-cyan-50 font-bold">{ev.payload.ticker} · {ev.payload.direction} · <span className="text-blue-400">E {ev.payload.entry}</span> <span className="text-emerald-400">T {ev.payload.target}</span> <span className="text-rose-400">S {ev.payload.stop}</span></p>
    <FloorChart data={ev.payload} panes height={240} testid="prakash-chart" />
  </div>
);

export const CramsDesk = ({ ev }) => {
  const ck = ev.payload.checks || [], n = useCount(ev.t_ms, ck.length, 450);
  return (
    <div className="space-y-1.5" data-testid="desk-crams">
      <p className="text-cyan-50 font-bold">{ev.payload.ticker}</p>
      {ck.slice(0, n).map((c) => (
        <div key={c.label} className="flex items-center gap-2 text-base nx-fade-up" data-testid={`crams-check-${c.label.replace(/\s+/g, "-").toLowerCase()}`}>
          {c.ok ? <CheckCircle2 className="w-5 h-5 text-emerald-400" /> : <XCircle className="w-5 h-5 text-rose-400" />}
          <span className="text-cyan-50 w-44">{c.label}</span><span className="text-cyan-200/50 text-sm">{c.detail}</span>
        </div>))}
    </div>
  );
};

export const DebateDesk = ({ byAgent, ticker }) => {
  const pick = (a) => (byAgent[a] || []).filter((e) => e.payload.ticker === ticker).at(-1);
  const bull = pick("london"), bear = pick("tariq"), risk = pick("jimmycool");
  return (
    <div className="space-y-2" data-testid="desk-debate">
      <p className="text-cyan-50 font-bold">{ticker}</p>
      <div className="grid grid-cols-2 gap-3 text-sm leading-snug">
        <p className="text-emerald-200/90 line-clamp-5 nx-fade-up" data-testid="debate-bull">{bull ? bull.payload.text : ""}</p>
        <p className="text-rose-200/90 line-clamp-5 nx-fade-up" data-testid="debate-bear">{bear ? bear.payload.text : ""}</p>
      </div>
      {risk && <p className="text-sm text-fuchsia-200/90 border-t border-white/10 pt-2 line-clamp-3 nx-fade-up" data-testid="debate-risk">Jimmy Cool: {risk.payload.text}</p>}
    </div>
  );
};

export const GenieDesk = ({ ev, list }) => {
  const ok = ev.payload.verdict === "APPROVED";
  return (
    <div className="space-y-2" data-testid="desk-genie">
      <div className="flex items-center gap-4 nx-fade-up">
        <span className={`nx-orbitron font-black text-3xl px-4 py-1 rounded-lg border-4 -rotate-3 ${ok ? "text-emerald-400 border-emerald-400" : "text-rose-400 border-rose-400"}`} data-testid="genie-stamp">{ev.payload.verdict}</span>
        <b className="text-2xl text-cyan-50">{ev.payload.ticker}</b>
      </div>
      <p className="text-sm text-cyan-100/80">{ev.payload.reason}</p>
      <p className="text-xs text-cyan-200/50">{list.slice(0, -1).map((e) => `${e.payload.ticker} ${e.payload.verdict}`).join(" · ")}</p>
    </div>
  );
};
